import React, { useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Column } from 'primereact/column'

import {AuthContext, GrowlContext} from 'Context'
import useCrudData from 'Hooks/useCrudData'
import useModalController from "Hooks/useModalController";

import {BadgeList, Button, LoadingSpinner, Modal, ToolPanel} from 'Components/readonly'
import {FilterTableContainer, FlexRow, TableContainer, ThinHeaderDataTable} from 'Components/layout'
import ChemicalFilter, { filterColors } from 'Components/combined/chemicalFilter'
import { SidebarWrapperMobile, SidebarWrapperPage } from 'Components/combined/sidebarWrapper'
import ProductModalWrapper from './ProductModalWrapper';
import { TableMarkings } from 'Components/readonly/markings'
import ArchiveProductDialog from './dialogs/archiveProductDialog'
import DownloadProductSDBDialog from './dialogs/downloadProductSDBDialog'
import _ from 'lodash'
import axios from "axios";

export const ChemicalList = ({ inputFilter, filter, history, archive, passProductCount, defaultSortOrder, first, setFirst, page, setPage, withOutTabs }) => {
  const intl = useIntl()
  const [selectProductPage, setSelectProductPage] = useState(false);
  const [deleteProductId, setDeleteProductId] = useState(null)
  const { user, organisation } = useContext(AuthContext)
  const [rows, setRows] = useState(localStorage.hasOwnProperty('numChemicalRows') ? parseInt(localStorage.getItem('numChemicalRows')) : 10)
  const [sort, setSort] = useState({ field: defaultSortOrder, order: -1 })
  const savedSortRef = useRef(null)
  const wasSearchingRef = useRef(false)
  const { displayError } = React.useContext(GrowlContext)
  const [downloadModalController] = useModalController({ title: intl.formatMessage({ id: 'sds.show-sds' }) })

  useEffect(() => {
    localStorage.setItem("numChemicalRows", rows);
  }, [rows]);

  useEffect(() => {
    const isSearching = Boolean(inputFilter)

    if (isSearching && !wasSearchingRef.current) {
      savedSortRef.current = { field: sort.field, order: sort.order }
    }

    if (!isSearching && wasSearchingRef.current && savedSortRef.current) {
      setSort(savedSortRef.current)
      savedSortRef.current = null
    }

    wasSearchingRef.current = isSearching
  }, [inputFilter, sort.field, sort.order])

  const getUrlParams = (page, rows, inputFilter) => {
    let filters = {
      candidate: filter.candidate,
    }
    let possibleFilters = [
      'he',
      'pd',
      'prio',
      'listingsList',
      'tagList',
      'remarkList',
      'location',
    ];

    _.each(possibleFilters, (filterProperty) => {
      if (filter[filterProperty] !== undefined && filter[filterProperty].length > 0) {
        filters[filterProperty] = _.map(filter[filterProperty], 'value');
      }
    })

    if (filter.locations.selectedKeys !== undefined && filter.locations.selectedKeys.length > 0) {
      filters.locations = filter.locations.selectedKeys;
    }

    const isSearching = Boolean(inputFilter)

    let urlParams = {
      page: page,
      filters: filters,
      numItems: rows,
      order_by: isSearching ? 'relevance' : sort.field,
      order_direction: isSearching ? -1 : sort.order,
    };

    if (inputFilter) {
      urlParams.search = inputFilter
    }

    if (filter.orFilter) {
      urlParams['or-filter'] = true
    }

    if (filters.length > 0) {
      urlParams.filters = filters;
    }

    urlParams.filters = JSON.stringify(urlParams.filters);

    return new URLSearchParams(urlParams).toString();
  }

  // eslint-disable-next-line
  const [isLoading, chemicalList, createProduct, readProduct, updateProduct, deleteProduct] = useCrudData(
    '/rapi/chemicals/organisation' +
    (archive ? '/archived' : '') +
    ('?' + getUrlParams(page, rows, inputFilter)),
    [],
    checks(),
    (data) => {
      passProductCount(archive ? null : (data.total ? data.total : 0));
      return data;
    },
    inputFilter?.length === 1 || inputFilter?.length === 2 // Add debounce functionality to avoid dissappearing content connected to MySQL 3 character search limit
  )

  function checks() {
    return [
      organisation,
      page,
      rows,
      inputFilter,
      sort.field,
      sort.order,
      filter.he,
      filter.pd,
      filter.prio,
      filter.candidate,
      filter.orFilter,
      filter.listingsList,
      filter.tagList,
      filter.remarkList,
      filter.locations?.selectedKeys,
    ]
  }

  const showProduct = (row) => {
    if (!archive) {
      // Opens ProductPage in a modal to retain filter options but keeps full-page
      // access through state change
      // history.push('/product/' + row.id)
      setSelectProductPage(row.id);
    }
  }

  const locationBody = (row) => {
    if(row.type === 'archive') {
      return row.location_name
    }
    return row.locationsDisplayString
  }

  const columns = [
    <Column key="article_number" field="article_number" header={intl.formatMessage({ id: 'chemicals.article_id' })}
            sortable={true}/>
  ]

  /**
   * Request will hide the archived row from results (Super admin only)
   *
   * @param row
   * @returns {Promise<void>}
   */
  const hideArchiveRow = async (row) => {
    row.hide_archive = (row.hide_archive === 1) ? 0 : 1;
    try {
      await axios.post('/rapi/chemicals/organisation/archived-hide/'+row.organisation_chemical_id)
    } catch (error) {
      displayError(error)
    }
  }

  /**
   * This will open a dialog/modal where the user can download the connected SDS
   *
   * @param row
   * @returns {JSX.Element}
   */
  const showProductBody = (row) => (
    row.type === 'chemical' ?
      <Button
        style={{margin: 0}}
        primary
        label={intl.formatMessage({ id: 'sds.show' })}
        icon="pi pi-arrow-circle-right"
        onClick={() => downloadModalController.open({ data: { productId: "" + row.id } })}
      />
    :
      <Button
        style={{margin: 0}}
        primary
        label={intl.formatMessage({ id: 'sds.show' })}
        icon="pi pi-arrow-circle-right"
        onClick={() => window.open('/rapi/chemicals/organisation/archived/' + row.archive_id + '/view', '_blank')}
      />
  )

  /**
   * Will add an eye icon button that has a hide archive click event bounded to it
   *
   * @param row
   * @returns {JSX.Element}
   */
  const updateArchiveBody = (row) => (
    row.type === 'chemical' && (
      <FlexRow key={row}>
        <Button
          primary
          icon="pi pi-eye"
          onClick={() => {
            hideArchiveRow(row);
          }}
        />
      </FlexRow>
    )
  )
  if (organisation.enable_chemical_list_article_numbers) {
    columns.push(
      <Column
        key="organisation_article_number"
        field="organisation_article_number"
        header={intl.formatMessage({ id: 'chemicals.organisation_article_number_short' })}
        sortable={true}
      />
    )
  }
  columns.push(
    <Column
      key="name"
      field="name"
      header={intl.formatMessage({ id: 'chemicals.product' })}
      sortable={true}
    />
  )
  if (organisation.enable_chemical_list_synonyms) {
    columns.push(
      <Column
        key="organisation_synonym"
        field="organisation_synonym"
        header={intl.formatMessage({ id: 'chemicals.organisation_synonym_short' })}
        sortable={true}
      />
    )
  }
  columns.push(
    <Column
      key="location"
      field="locationsDisplayString"
      header={intl.formatMessage({ id: 'common.location' })}
      body={locationBody}
      sortable={false}
    />,
    <Column
      key="supplier"
      field="supplier"
      header={intl.formatMessage({ id: 'common.supplier' })}
      sortable={true}
    />
  )

  if (!archive) {
    columns.push(
      <Column
        key="status_text"
        field="status_text"
        header={intl.formatMessage({ id: 'common.status' })}
        sortable={true}/>
    )
  }

  columns.push(
    <Column
      key="markings"
      style={{ width: '130px' }}
      body={TableMarkings}
      header={intl.formatMessage({ id: 'common.marking' })}/>
  )

  columns.push(
    <Column
      key="organisation_added_at"
      field="organisation_added_at"
      header={intl.formatMessage({ id: 'chemicals.added_at' })}
      sortable={true}
    />
  )

  if (!archive) {
    columns.push(
      <Column
        key="sds_date"
        field="sds_date"
        header={intl.formatMessage({ id: 'chemicals.sds_date' })}
        sortable={true}
      />
    )
  }

  if (archive) {
    columns.push(
      <Column
        key="organisation_removed_at"
        field="organisation_removed_at"
        header={intl.formatMessage({ id: 'chemicals.removed_at' })}
        sortable={true}
      />
    )

    columns.push(
      <Column
        bodyStyle={{ textOverflow: 'unset', overflow: 'visible', whiteSpace: 'normal' }}
        sortable={false}
        body={showProductBody}
      />
    )
    if(user.isSuperAdmin) {
      columns.push(
        <Column
          style={{width: "70px"}}
          key="hide_archive"
          body={updateArchiveBody}
          header={intl.formatMessage({ id: 'common.hide' })}
          sortable={false}
        />
      )
    }
  }

  const updateLocationFilter = (updatedList) => {
    let locations = { ...filter.locations }
    let index = -1
    locations.locationNames.forEach((item, i) => {
      if (_.findIndex(updatedList, { label: item }) === -1) {
        index = i
      }
    })
    locations.locationNames.splice(index, 1)
    locations.selectedKeys.splice(index, 1)
    filter.setLocations(locations)
  }

  const dataTableRef = useRef()
  const exportCSVexternal = () => {
    if (archive) {
      const queryStr = getUrlParams(page, rows, inputFilter);
      window.open('/rapi/chemicals/csv/export/archived?' + queryStr);
    } else {
      const queryStr = getUrlParams(page, rows, inputFilter);
      window.open('/rapi/chemicals/csv/export?' + queryStr + '&numItems=' + chemicalList.total);
    }
  }

  const exportSds = () => {
    window.open('/rapi/chemicals/sds/export')
  }

  const ChemicalTable = () => {
    return (
        <ThinHeaderDataTable
        ref={dataTableRef}
        className={'flextable ' + !archive ? 'clickable-rows' : ''}
        value={chemicalList.data}
        paginator={true}
        rows={rows}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        totalRecords={chemicalList.total ? chemicalList.total : 0}
        lazy={true}
        first={first}
        sortField={inputFilter ? null : sort.field}
        sortOrder={inputFilter ? null : sort.order}
        onSort={(e) => {
          setSort({ field: e.sortField, order: e.sortOrder })
          setFirst(0)
          setPage(1)
        }}
        onPage={(e) => {
          setFirst(e.first)
          setRows(e.rows)
          setPage(e.page + 1)
        }}
        rowClassName={(row) => {
          return {
            disabled: archive,
            'hide-archive': (row.hide_archive === 1)
          }
        }}
        onRowClick={(event) => {
          showProduct(event.data)
        }}>
        {columns}
      </ThinHeaderDataTable>
    )
  }

  return (
    <>
      <Modal
        modalController={downloadModalController}
        ContentComponent={DownloadProductSDBDialog}
        buttonTitle={intl.formatMessage({ id: 'sds.show' })}
      />
      {selectProductPage && (
        <ProductModalWrapper withOutTabs={withOutTabs ?? false} productId={selectProductPage} onClose={() => setSelectProductPage(false)} />
      )}
      <ArchiveProductDialog
        isOpen={deleteProductId !== null}
        productId={deleteProductId}
        close={() => setDeleteProductId(null)}
        deleteProduct={deleteProduct}
      />
      <ToolPanel>
        <SidebarWrapperMobile icon="pi pi-filter" buttonText={intl.formatMessage({ id: 'common.filter' })}>
          <ChemicalFilter filter={filter} setFirst={setFirst}/>
        </SidebarWrapperMobile>

        <Button icon="pi pi-file-excel" label={intl.formatMessage({ id: 'admin.export_csv' })} onClick={exportCSVexternal}/>
        {user.isSuperAdmin && (
          <Button icon="pi pi-file-pdf" label={intl.formatMessage({ id: 'chemicals.export_sds' })} onClick={exportSds}/>
        )}
      </ToolPanel>
      <FilterTableContainer>
        <TableContainer>
          <BadgeList
            sources={[
              { value: filter.he, color: filterColors.he, update: filter.setHe },
              { value: filter.pd, color: filterColors.pd, update: filter.setPd },
              { value: filter.prio, color: filterColors.prio, update: filter.setPrio },
              {
                value: filter.candidate ? [{ label: intl.formatMessage({ id: 'chemicals.form.ingredient.listed_candidate' }) }] : [],
                color: filterColors.candidate,
                update: filter.handleSetCandidate
              },
              {
                value: filter.locations.locationNames ? filter.locations.locationNames.map((l) => ({ label: l })) : [],
                color: filterColors.location,
                update: updateLocationFilter
              },
              { value: filter.tagList, color: filterColors.tagList, update: filter.setTagList },
              { value: filter.listingsList, color: filterColors.listingsList, update: filter.setListingsList },
              { value: filter.remarkList, color: filterColors.remarkList, update: filter.setRemarkList }
            ]}
          />
          <LoadingSpinner isLoading={isLoading}>
            <ChemicalTable/>
          </LoadingSpinner>
        </TableContainer>
        <SidebarWrapperPage>
          <ChemicalFilter filter={filter} setFirst={setFirst}/>
        </SidebarWrapperPage>
      </FilterTableContainer>
    </>
  )
}
